import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"

import { cn } from "@/lib/utils"

const FullscreenDialog = DialogPrimitive.Root

const FullscreenDialogTrigger = DialogPrimitive.Trigger

const FullscreenDialogPortal = DialogPrimitive.Portal

const FullscreenDialogClose = DialogPrimitive.Close

const FullscreenDialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-background/80 backdrop-blur-sm",
      "duration-200",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
      className
    )}
    {...props}
  />
))
FullscreenDialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const FullscreenDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <FullscreenDialogPortal>
    <FullscreenDialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed inset-0 z-50 flex flex-col bg-background",
        "duration-200",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
        className
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </FullscreenDialogPortal>
))
FullscreenDialogContent.displayName = DialogPrimitive.Content.displayName

const FullscreenDialogHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex items-center justify-between border-b border-dialog-header-border bg-dialog-header text-dialog-header-foreground px-4 md:px-6 py-2",
      className
    )}
    {...props}
  />
))
FullscreenDialogHeader.displayName = "FullscreenDialogHeader"

const FullscreenDialogBody = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex-1 overflow-y-auto",
      className
    )}
    {...props}
  />
))
FullscreenDialogBody.displayName = "FullscreenDialogBody"

const FullscreenDialogFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex items-center justify-end gap-2 border-t border-border bg-muted px-4 md:px-6 py-2",
      className
    )}
    {...props}
  />
))
FullscreenDialogFooter.displayName = "FullscreenDialogFooter"

const FullscreenDialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-xl font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
FullscreenDialogTitle.displayName = DialogPrimitive.Title.displayName

const FullscreenDialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
FullscreenDialogDescription.displayName =
  DialogPrimitive.Description.displayName

export {
  FullscreenDialog,
  FullscreenDialogPortal,
  FullscreenDialogOverlay,
  FullscreenDialogClose,
  FullscreenDialogTrigger,
  FullscreenDialogContent,
  FullscreenDialogHeader,
  FullscreenDialogBody,
  FullscreenDialogFooter,
  FullscreenDialogTitle,
  FullscreenDialogDescription,
}
